import * as React from "react"
import * as SelectPrimitive from "@radix-ui/react-select"
import { Check, ChevronDown } from "@untitledui/icons"
import { cx } from "@/lib/utils/cx"

// Radix-based select that matches the new design vocabulary — rounded-xl
// trigger, popover-style content with rounded-2xl + soft shadow, emerald
// check pill on the active item. Replaces native `<select>` elements one at
// a time.
//
// Usage:
//
//   <DesignSelect.Root value={x} onValueChange={setX}>
//     <DesignSelect.Trigger>
//       <DesignSelect.Value placeholder="..." />
//     </DesignSelect.Trigger>
//     <DesignSelect.Content>
//       <DesignSelect.Item value="all">All</DesignSelect.Item>
//       <DesignSelect.Item value="open">Open</DesignSelect.Item>
//     </DesignSelect.Content>
//   </DesignSelect.Root>

const Root = SelectPrimitive.Root
const Group = SelectPrimitive.Group
const Value = SelectPrimitive.Value

const Trigger = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
    leadingIcon?: React.ReactNode
  }
>(({ className, children, leadingIcon, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cx(
      "h-10 px-3 rounded-xl border border-zinc-200 bg-zinc-50 text-xs md:text-sm font-medium text-zinc-700 outline-none transition-all duration-150",
      "hover:border-zinc-300 hover:bg-white",
      "data-[state=open]:bg-white data-[state=open]:border-zinc-300",
      "focus-visible:ring-2 focus-visible:ring-zinc-300/60 focus-visible:border-zinc-400",
      "flex items-center gap-2 shrink-0",
      "[&>span]:truncate",
      className,
    )}
    {...props}
  >
    {leadingIcon}
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="size-3.5 text-zinc-400 shrink-0" aria-hidden />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
))
Trigger.displayName = SelectPrimitive.Trigger.displayName

const Content = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", sideOffset = 6, ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      position={position}
      sideOffset={sideOffset}
      className={cx(
        "z-50 min-w-[var(--radix-select-trigger-width)] rounded-2xl border border-zinc-200/70 bg-white shadow-2xl shadow-zinc-900/15 overflow-hidden p-1.5",
        "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
        "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
        "data-[side=bottom]:slide-in-from-top-1 data-[side=top]:slide-in-from-bottom-1",
        className,
      )}
      {...props}
    >
      <SelectPrimitive.Viewport className="max-h-[360px]">
        {children}
      </SelectPrimitive.Viewport>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
))
Content.displayName = SelectPrimitive.Content.displayName

const Item = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
    leadingIcon?: React.ReactNode
    /** Second line under the label (e.g. "Service Advisor · Service") —
     *  detail only; the trigger still shows just the label. */
    sub?: string
  }
>(({ className, children, leadingIcon, sub, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cx(
      "relative flex w-full items-center gap-2.5 px-2.5 py-2 rounded-xl text-[13px] font-medium text-zinc-700 cursor-pointer outline-none select-none",
      "data-[highlighted]:bg-zinc-50 data-[highlighted]:text-zinc-900",
      "data-[state=checked]:bg-emerald-50/70 data-[state=checked]:text-emerald-900",
      "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className,
    )}
    {...props}
  >
    {leadingIcon}
    <span className="flex-1 min-w-0">
      <SelectPrimitive.ItemText className="block truncate">{children}</SelectPrimitive.ItemText>
      {sub && (
        <span className="block text-[11px] font-normal text-zinc-400 truncate">{sub}</span>
      )}
    </span>
    <SelectPrimitive.ItemIndicator className="ml-auto">
      <span className="size-5 rounded-full bg-emerald-500 grid place-items-center shadow-sm shadow-emerald-600/30">
        <Check className="size-3 text-white" strokeWidth={3} aria-hidden />
      </span>
    </SelectPrimitive.ItemIndicator>
  </SelectPrimitive.Item>
))
Item.displayName = SelectPrimitive.Item.displayName

export const DesignSelect = {
  Root,
  Group,
  Value,
  Trigger,
  Content,
  Item,
}
